import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Radio,
  RadioGroup,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SmartToyIcon from "@mui/icons-material/SmartToy";

import { PERMISSIONS } from "../../../constants";
import { getReciversUser, setUserRole } from "../../../services/backend/UserService";
import PqrsdDocuments from "./PqrsdDocuments";
import PqrsdSurveyConfig from "./PqrsdSurveyConfig";
import { updateUsers } from "../../../redux/slices/userSlice";
import emailTemplates from "../../../services/backend/gfunctions/pqrsd/_shared/emailTemplates";
import {
  DEFAULT_PQRSD_CONFIG,
  createPqrsdGroup,
  deletePqrsdGroup,
  getPqrsdConfig,
  savePqrsdConfig,
  subscribePqrsdGroups,
  updatePqrsdGroup,
} from "../../../services/backend/PqrsdConfigService";

import { applyPqrsdLabel, getPqrsdLabel, pqrsdText } from "../pqrsdLabel";
import {
  DEFAULT_MODULE_LABEL,
  MAX_MODULE_LABEL,
  moduleLabelError,
  moduleSlug,
  normalizeModuleLabel,
} from "../../../services/backend/gfunctions/pqrsd/_shared/moduleLabel";
import {
  DEFAULT_REOPEN_POLICY,
  MAX_FIXED_DAYS,
  MAX_REOPENS_CAP,
  REOPEN_TERM_HELP,
  REOPEN_TERM_LABELS,
  REOPEN_TERM_MODES,
  normalizeReopenPolicy,
  validateReopenPolicy,
} from "../../../services/backend/gfunctions/pqrsd/_shared/reopenPolicy";
// Coerces the interim-cap input into a stored value: 0 = unlimited, otherwise
// an integer floored at 1. Blank/invalid falls back to the default (2).
function normalizeMaxInterims(raw) {
  if (raw === 0 || raw === "0") return 0;
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n) || n < 1) return DEFAULT_PQRSD_CONFIG.maxInterimsPerCycle;
  return n;
}

// Requerimiento de información (CPACA art. 17). 0 = unlimited requerimientos;
// the reply window can be widened but never cut below the legal month.
function normalizeMaxInfoRequests(raw) {
  if (raw === 0 || raw === "0") return 0;
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n) || n < 1) return DEFAULT_PQRSD_CONFIG.maxInfoRequestsPerCycle;
  return n;
}

function normalizeCitizenDays(raw, fallback) {
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n) || n < fallback) return fallback;
  return n;
}

// Role auto-managed from the dependency editor: being a dependency lead grants
// it, no longer leading any dependency revokes it.
const DEP_SUPERVISOR_ROLE = "PQRSD_DEPENDENCY_SUPERVISOR";

// Admin module to tune the PQRSD AI classifier: CRUD the dependency groups it
// routes to (each with a description that IS the routing context the model
// reads) and the global classifier settings. Gated by PQRSD.CONFIG.
// Message carried across the remount that follows a rename. The route guard
// can mount the page more than once while it re-checks the session, so it is
// not consumed on first read: it simply expires a few seconds later.
let flash = null;
const readFlash = () => (flash && flash.until > Date.now() ? flash.feedback : null);

export default function PqrsdConfig() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.value);
  // Authoritative user list (carries roles/permissions) — same source UserAdmin
  // edits, so the role sync reads/writes consistent data.
  const allUsers = useSelector((state) => state.user.users);
  const canConfig = (user?.permissions || []).includes(PERMISSIONS.PQRSD.CONFIG);
  // Sensitive infra settings (AI model, portal base URL) are only exposed to
  // full admins — a config-permitted operator tunes routing, not infra.
  const isFullAdmin = !!(user?.roles || []).includes("ADMIN");

  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { mode: 'create'|'edit', group }
  // A rename remounts the router (see App); the message survives it.
  const [feedback, setFeedback] = useState(readFlash);
  const [tab, setTab] = useState("general"); // 'general' | 'documentos' | 'encuesta' | 'ia' | 'correos' | 'grupos'

  // Global config form state.
  const [cfg, setCfg] = useState(DEFAULT_PQRSD_CONFIG);
  // The reopen rule shown: a missing or unknown stored value reads as the default.
  const reopenTermMode = normalizeReopenPolicy(cfg).reopenTermMode;
  const [savingCfg, setSavingCfg] = useState(false);
  useEffect(() => {
    if (!canConfig) return undefined;
    const unsub = subscribePqrsdGroups((list) => {
      setGroups(list);
      setLoading(false);
    });
    return () => unsub && unsub();
  }, [canConfig]);

  useEffect(() => {
    if (!canConfig) return;
    (async () => {
      setCfg(await getPqrsdConfig());
      if (user?.token) {
        const res = await getReciversUser(user.token);
        const list = Array.isArray(res) ? res : res?.users || res?.result || [];
        setUsers(Array.isArray(list) ? list : []);
      }
    })();
  }, [canConfig, user?.token]);

  const saveConfig = async () => {
    const labelError = moduleLabelError(cfg.moduleLabel ?? DEFAULT_MODULE_LABEL);
    if (labelError) {
      setFeedback({ severity: "error", msg: `Nombre del módulo: ${labelError}` });
      return;
    }
    const reopenDraft = {
      reopenTermMode: reopenTermMode,
      reopenFixedDays: Number(cfg.reopenFixedDays ?? DEFAULT_REOPEN_POLICY.reopenFixedDays),
      maxReopens: Number(cfg.maxReopens ?? DEFAULT_REOPEN_POLICY.maxReopens),
    };
    const reopenErrors = validateReopenPolicy(reopenDraft);
    if (reopenErrors.length) {
      setFeedback({ severity: "error", msg: `Reapertura: ${reopenErrors.join(" ")}` });
      return;
    }
    setSavingCfg(true);
    const moduleLabel = normalizeModuleLabel(cfg.moduleLabel);
    const patch = {
      // How the module is called everywhere (pqrsdText, email templates).
      moduleLabel,
      // What a citizen's reopening does to the term, and how many are allowed.
      ...normalizeReopenPolicy(reopenDraft),
      // Per-institution policy: editable by any PQRSD.CONFIG operator (not gated
      // by full-admin). 0 = unlimited; otherwise floor at 1.
      maxInterimsPerCycle: normalizeMaxInterims(cfg.maxInterimsPerCycle),
      maxInfoRequestsPerCycle: normalizeMaxInfoRequests(cfg.maxInfoRequestsPerCycle),
      citizenReplyDays: normalizeCitizenDays(cfg.citizenReplyDays, DEFAULT_PQRSD_CONFIG.citizenReplyDays),
      citizenExtensionDays: normalizeCitizenDays(cfg.citizenExtensionDays, DEFAULT_PQRSD_CONFIG.citizenExtensionDays),
      infoRequestRequiresApproval: !!cfg.infoRequestRequiresApproval,
    };
    // The AI classifier settings (and other sensitive infra fields) are full-admin
    // only; omitting them for non-admins lets the merge preserve whatever is
    // stored so a PQRSD.CONFIG operator can't write or clobber the IA config.
    if (isFullAdmin) {
      patch.aiEnabled = !!cfg.aiEnabled;
      patch.systemPrompt = cfg.systemPrompt || "";
      patch.priorityCriteria = cfg.priorityCriteria || "";
      patch.confidenceThreshold = Number(cfg.confidenceThreshold) || DEFAULT_PQRSD_CONFIG.confidenceThreshold;
      patch.readAttachments = !!cfg.readAttachments;
      patch.maxAttachmentBytes = Number(cfg.maxAttachmentBytes) || DEFAULT_PQRSD_CONFIG.maxAttachmentBytes;
      patch.model = (cfg.model || "").trim() || DEFAULT_PQRSD_CONFIG.model;
      // Marca, logo y URL base son ajustes generales: se editan en
      // Configuración → Ajustes generales (config/global), no aquí.
    }
    // A rename remounts this page before the save even resolves: Firestore's
    // local write fires the config listener first. So the message is staged
    // before saving, and dropped if the save fails.
    const renaming = moduleLabel !== getPqrsdLabel();
    if (renaming) {
      flash = {
        until: Date.now() + 8000,
        feedback: { severity: "success", msg: `Configuración guardada. El módulo ahora se llama «${moduleLabel}».` },
      };
    }
    const ok = await savePqrsdConfig(patch);
    setSavingCfg(false);
    if (!ok) flash = null;
    // Rename right away for this admin; everyone else follows the config doc.
    if (ok && renaming) applyPqrsdLabel(moduleLabel);
    setFeedback(ok
      ? { severity: "success", msg: "Configuración guardada." }
      : { severity: "error", msg: "No fue posible guardar la configuración." });
  };

  // Keeps the PQRSD_DEPENDENCY_SUPERVISOR role in sync with dependency leadership:
  // being added as a supervisor grants the role; being removed revokes it, but
  // only when the user no longer leads any other dependency. Best-effort — the
  // group is already saved by the time this runs; failures surface as a warning.
  // `savedGroupId` is the just-saved group (excluded from the "leads elsewhere"
  // check since its supervisorIds already reflect the change).
  const syncSupervisorRoles = async ({ added, removed, savedGroupId }) => {
    if (!added.length && !removed.length) return;
    const byId = new Map((allUsers || []).map((u) => [u.userId, u]));
    try {
      for (const id of added) {
        const u = byId.get(id);
        if (!u) continue;
        const updated = await setUserRole(u, DEP_SUPERVISOR_ROLE, true, user);
        if (updated !== u) dispatch(updateUsers(updated));
      }
      for (const id of removed) {
        const leadsElsewhere = groups.some(
          (g) => g.id !== savedGroupId && Array.isArray(g.supervisorIds) && g.supervisorIds.includes(id)
        );
        if (leadsElsewhere) continue;
        const u = byId.get(id);
        if (!u) continue;
        const updated = await setUserRole(u, DEP_SUPERVISOR_ROLE, false, user);
        if (updated !== u) dispatch(updateUsers(updated));
      }
    } catch (e) {
      setFeedback({
        severity: "warning",
        msg: `Dependencia guardada, pero no se pudo sincronizar el rol de supervisor: ${e.message}`,
      });
    }
  };

  if (!canConfig) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6">Sin acceso</Typography>
        <Typography variant="body2" color="text.secondary">
          {pqrsdText("No tienes permiso para configurar el módulo PQRSD IA.")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <SmartToyIcon color="primary" />
        <Typography variant="h4">{pqrsdText("Config PQRSD")}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Afina el clasificador de IA, personaliza los correos y define las dependencias de enrutamiento.
      </Typography>

      {feedback && (
        <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.msg}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab value="general" label="General" />
        <Tab value="documentos" label="Documentos" />
        <Tab value="encuesta" label="Encuesta" />
        {isFullAdmin && <Tab value="ia" label="IA" />}
        {isFullAdmin && <Tab value="correos" label="Correos" />}
        <Tab value="grupos" label="Dependencias" />
      </Tabs>

      {/* --- General tab: per-institution PQRSD policy (available to any
             PQRSD.CONFIG operator, not just full admins) --- */}
      {tab === "general" && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Nombre del módulo</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Cómo se llama el módulo en toda la plataforma: menús, pantallas, la página pública de
              radicación y consulta, y los correos. Los números de radicado y los documentos legales
              no cambian.
            </Typography>
            <TextField
              label="Nombre del módulo"
              value={cfg.moduleLabel ?? DEFAULT_MODULE_LABEL}
              onChange={(e) => setCfg((c) => ({ ...c, moduleLabel: e.target.value }))}
              error={!!moduleLabelError(cfg.moduleLabel ?? DEFAULT_MODULE_LABEL)}
              helperText={
                moduleLabelError(cfg.moduleLabel ?? DEFAULT_MODULE_LABEL)
                || `Ejemplo: "Consultar el estado de su ${normalizeModuleLabel(cfg.moduleLabel)}". `
                  + `Página pública: ${window.location.origin}/${moduleSlug(cfg.moduleLabel)}. Máximo ${MAX_MODULE_LABEL} caracteres.`
              }
              inputProps={{ maxLength: MAX_MODULE_LABEL + 10 }}
              sx={{ maxWidth: 420, mb: 3 }}
              fullWidth
            />

            <Typography variant="h6" gutterBottom>Política de respuestas</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {pqrsdText("Ajustes propios de cada institución para el trámite de las PQRSD.")}
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={Number(cfg.maxInterimsPerCycle) === 0}
                  onChange={(e) =>
                    setCfg((c) => ({
                      ...c,
                      maxInterimsPerCycle: e.target.checked
                        ? 0
                        : DEFAULT_PQRSD_CONFIG.maxInterimsPerCycle,
                    }))
                  }
                />
              }
              label="Sin límite de respuestas interinas (ilimitado)"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              {pqrsdText("Una respuesta interina (informe de avance) reinicia el plazo y mantiene la PQRSD abierta. El ciclo se reinicia con cada respuesta final.")}
            </Typography>

            {Number(cfg.maxInterimsPerCycle) !== 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth type="number" label="Máx. respuestas interinas por ciclo"
                    inputProps={{ min: 1, step: 1 }}
                    value={cfg.maxInterimsPerCycle ?? DEFAULT_PQRSD_CONFIG.maxInterimsPerCycle}
                    onChange={(e) =>
                      setCfg((c) => ({ ...c, maxInterimsPerCycle: e.target.value }))
                    }
                    helperText="Mínimo 1. Al alcanzarlo, la siguiente respuesta debe ser final."
                  />
                </Grid>
              </Grid>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>Reapertura por el ciudadano</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Qué pasa con el plazo cuando el ciudadano no queda satisfecho con la respuesta final y
              reabre la solicitud. La Ley 1755 no regula la reapertura: es una decisión de la entidad.
            </Typography>
            <RadioGroup
              value={reopenTermMode}
              onChange={(e) => setCfg((c) => ({ ...c, reopenTermMode: e.target.value }))}
            >
              {REOPEN_TERM_MODES.map((mode) => (
                <FormControlLabel
                  key={mode}
                  value={mode}
                  control={<Radio size="small" />}
                  sx={{ alignItems: "flex-start", mb: 1, "& .MuiRadio-root": { pt: 0.25 } }}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {REOPEN_TERM_LABELS[mode]}{mode === "reset" ? " (predeterminado)" : ""}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{pqrsdText(REOPEN_TERM_HELP[mode])}</Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              {reopenTermMode === "fixed" && (
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth type="number" label="Días hábiles al reabrir"
                    value={cfg.reopenFixedDays ?? DEFAULT_REOPEN_POLICY.reopenFixedDays}
                    onChange={(e) => setCfg((c) => ({ ...c, reopenFixedDays: e.target.value }))}
                    inputProps={{ min: 1, max: MAX_FIXED_DAYS, step: 1 }}
                    helperText={`Entre 1 y ${MAX_FIXED_DAYS}, contados desde la reapertura.`}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth type="number" label="Máximo de reaperturas por solicitud"
                  value={cfg.maxReopens ?? DEFAULT_REOPEN_POLICY.maxReopens}
                  onChange={(e) => setCfg((c) => ({ ...c, maxReopens: e.target.value }))}
                  inputProps={{ min: 0, max: MAX_REOPENS_CAP, step: 1 }}
                  helperText="0 = sin límite. Al alcanzarlo, el ciudadano ya no ve la opción de reabrir."
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>Requerimiento de información al ciudadano</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Cuando una petición está incompleta, la entidad requiere al ciudadano dentro de los 10
              días siguientes a la radicación. El término de respuesta queda suspendido hasta que
              responda (artículo 17 de la Ley 1755 de 2015).
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth type="number" label={pqrsdText("Máx. requerimientos por PQRSD")}
                  inputProps={{ min: 0, step: 1 }}
                  value={cfg.maxInfoRequestsPerCycle ?? DEFAULT_PQRSD_CONFIG.maxInfoRequestsPerCycle}
                  onChange={(e) => setCfg((c) => ({ ...c, maxInfoRequestsPerCycle: e.target.value }))}
                  helperText="0 = sin límite. La norma contempla un solo requerimiento."
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth type="number" label="Días calendario para responder"
                  inputProps={{ min: DEFAULT_PQRSD_CONFIG.citizenReplyDays, step: 1 }}
                  value={cfg.citizenReplyDays ?? DEFAULT_PQRSD_CONFIG.citizenReplyDays}
                  onChange={(e) => setCfg((c) => ({ ...c, citizenReplyDays: e.target.value }))}
                  helperText="Mínimo 30 (un mes). Puede ampliarse, nunca reducirse."
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth type="number" label="Días de prórroga"
                  inputProps={{ min: 1, step: 1 }}
                  value={cfg.citizenExtensionDays ?? DEFAULT_PQRSD_CONFIG.citizenExtensionDays}
                  onChange={(e) => setCfg((c) => ({ ...c, citizenExtensionDays: e.target.value }))}
                  helperText="Se concede a solicitud del ciudadano, hasta por un término igual."
                />
              </Grid>
            </Grid>

            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={!!cfg.infoRequestRequiresApproval}
                  onChange={(e) => setCfg((c) => ({ ...c, infoRequestRequiresApproval: e.target.checked }))}
                />
              }
              label="Exigir aprobación antes de enviar el requerimiento"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Con la revisión activada, el requerimiento queda pendiente hasta que otro funcionario
              con permiso de aprobación lo apruebe; el término sigue corriendo mientras tanto.
            </Typography>

            <Box sx={{ mt: 3, textAlign: "right" }}>
              <Button variant="contained" onClick={saveConfig} disabled={savingCfg}>
                {savingCfg ? "Guardando…" : "Guardar ajustes"}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* --- Documentos tab: membretes + the text of each document the module
             files by itself (any PQRSD.CONFIG operator) --- */}
      {tab === "documentos" && (
        <PqrsdDocuments
          documents={cfg.documents}
          onDocumentsChange={(documents) => setCfg((c) => ({ ...c, documents }))}
          onFeedback={setFeedback}
        />
      )}

      {/* --- Encuesta tab: the citizen satisfaction survey (any PQRSD.CONFIG
             operator), saved on its own --- */}
      {tab === "encuesta" && (
        <PqrsdSurveyConfig
          survey={cfg.citizenSurvey}
          onSurveyChange={(citizenSurvey) => setCfg((c) => ({ ...c, citizenSurvey }))}
          onFeedback={setFeedback}
        />
      )}

      {/* --- IA tab: classifier settings (full admins only) --- */}
      {tab === "ia" && isFullAdmin && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Ajustes del clasificador</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={!!cfg.aiEnabled}
                  onChange={(e) => setCfg((c) => ({ ...c, aiEnabled: e.target.checked }))}
                />
              }
              label="Clasificación automática con IA activada"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Con esta opción desactivada, la radicación funciona exactamente como antes (sin IA, sin enrutamiento por grupo).
            </Typography>

            <Grid container spacing={2}>
              {isFullAdmin && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth label="Modelo" value={cfg.model || ""}
                    onChange={(e) => setCfg((c) => ({ ...c, model: e.target.value }))}
                    helperText="Debe admitir visión (PDF/imágenes), p. ej. gpt-4o-mini."
                  />
                </Grid>
              )}
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth type="number" label="Umbral de confianza"
                  inputProps={{ min: 0, max: 1, step: 0.05 }}
                  value={cfg.confidenceThreshold ?? 0.7}
                  onChange={(e) => setCfg((c) => ({ ...c, confidenceThreshold: e.target.value }))}
                  helperText="Debajo de este valor → Triage."
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth type="number" label="Tamaño máx. adjunto (MB)"
                  inputProps={{ min: 1, step: 1 }}
                  value={Math.round((Number(cfg.maxAttachmentBytes) || 0) / 1024 / 1024) || 8}
                  onChange={(e) => setCfg((c) => ({ ...c, maxAttachmentBytes: (Number(e.target.value) || 8) * 1024 * 1024 }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth multiline minRows={3} label="Instrucción global (system prompt)"
                  value={cfg.systemPrompt || ""}
                  onChange={(e) => setCfg((c) => ({ ...c, systemPrompt: e.target.value }))}
                  helperText="Contexto general que envuelve las descripciones de las dependencias. Opcional."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth multiline minRows={2} label="Criterios de priorización"
                  value={cfg.priorityCriteria || ""}
                  onChange={(e) => setCfg((c) => ({ ...c, priorityCriteria: e.target.value }))}
                  helperText="Guía para asignar baja/media/alta/urgente (p. ej. riesgo a la vida, vencimientos legales)."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!cfg.readAttachments}
                      onChange={(e) => setCfg((c) => ({ ...c, readAttachments: e.target.checked }))}
                    />
                  }
                  label="Analizar adjuntos (PDF, imágenes, Word, Excel)"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, textAlign: "right" }}>
              <Button variant="contained" onClick={saveConfig} disabled={savingCfg}>
                {savingCfg ? "Guardando…" : "Guardar ajustes"}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* --- Correos tab: branding + live preview (full admins) --- */}
      {tab === "correos" && isFullAdmin && (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            La marca, el logo, la URL base del portal y el nombre de la institución ahora se
            configuran en <strong>Configuración → Ajustes generales</strong>{pqrsdText(" (aplican a toda la plataforma). Aquí solo puedes previsualizar cómo se ven los correos de PQRSD con la marca actual.")}
          </Alert>

          <EmailPreview
            brandColor={cfg.brandColor}
            brandName={cfg.brandName}
            logoUrl={cfg.logoUrl}
            logoWidth={cfg.logoWidth}
          />
        </>
      )}

      {/* --- Dependencias tab: routing groups --- */}
      {tab === "grupos" && (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="h6">Dependencias (grupos)</Typography>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setDialog({ mode: "create" })}>
              Nueva dependencia
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            La <strong>descripción</strong> de cada grupo es el contexto que la IA usa para enrutar. Sé específico sobre qué atiende cada dependencia.
          </Typography>

          {loading ? (
            <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>
          ) : groups.length === 0 ? (
            <Alert severity="info">
              {pqrsdText("Aún no hay dependencias. Crea al menos una para que la IA pueda enrutar (mientras tanto las PQRSD llegan a Triage).")}
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {groups.map((g) => (
                <Grid item xs={12} md={6} key={g.id}>
                  <GroupCard
                    group={g}
                    onEdit={() => setDialog({ mode: "edit", group: g })}
                    onDelete={async () => {
                      if (!window.confirm(`¿Eliminar la dependencia "${g.name}"?`)) return;
                      await deletePqrsdGroup(g.id);
                      setFeedback({ severity: "success", msg: "Dependencia eliminada." });
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {dialog && (
        <GroupDialog
          mode={dialog.mode}
          group={dialog.group}
          users={users}
          onSyncSupervisorRoles={syncSupervisorRoles}
          onClose={() => setDialog(null)}
          onSaved={(msg) => { setDialog(null); setFeedback({ severity: "success", msg }); }}
        />
      )}
    </Box>
  );
}

// Live email preview so a full admin sees exactly how notifications render with
// the chosen brand color before any is sent. Renders the real _shared template
// builders (the same the client and cloud functions use) into a sandboxed
// iframe. Passing the picked color through emailTemplates.setBrand mirrors what
// the senders do; a blank color falls back to the env/default chain, so the
// preview is faithful to what would actually go out.
const PREVIEW_TEMPLATES = [
  { key: "citizen_submission", label: "Ciudadano · Radicación recibida",
    build: (t) => t.citizenSubmission({
      pqrsd: { type: "queja", subject: "Ruido en la vía pública", slaDeadline: new Date(Date.now() + 12 * 864e5) },
      radicado: "2026-000123", magicLink: "#", referenceId: "REF-123",
    }) },
  { key: "citizen_in_progress", label: "Ciudadano · En trámite",
    build: (t) => t.citizenInProgress({ radicado: "2026-000123", magicLink: "#" }) },
  { key: "citizen_response_final", label: "Ciudadano · Respuesta final",
    build: (t) => t.citizenResponseFinal({ radicado: "2026-000123", magicLink: "#", confirmLink: "#", moreInfoLink: "#" }) },
  { key: "citizen_sla_warning", label: "Ciudadano · Aviso de plazo",
    build: (t) => t.citizenSlaWarning({ radicado: "2026-000123", slaDeadline: new Date(Date.now() + 2 * 864e5), magicLink: "#" }) },
  { key: "citizen_info_requested", label: "Ciudadano · Requerimiento de información",
    build: (t) => t.citizenInfoRequested({
      radicado: "2026-000123",
      items: [
        { kind: "question", header: "¿Cuál es la dirección exacta del predio?", required: true },
        { kind: "file", label: "Cédula", help: "Ambas caras en un solo PDF", required: true },
      ],
      dueAt: new Date(Date.now() + 30 * 864e5), replyLink: "#",
      note: "Sin estos datos no podemos identificar el predio.",
    }) },
  { key: "citizen_info_reminder", label: "Ciudadano · Recordatorio de información",
    build: (t) => t.citizenInfoReminder({
      radicado: "2026-000123", dueAt: new Date(Date.now() + 2 * 864e5), replyLink: "#", daysLeft: 2,
    }) },
  { key: "citizen_desistimiento", label: "Ciudadano · Desistimiento y archivo",
    build: (t) => t.citizenDesistimiento({
      radicado: "2026-000123", magicLink: "#", appealLink: "#",
      appealLastDay: new Date(Date.now() + 14 * 864e5),
      reason: "No se aportó la información requerida dentro del plazo legal.",
    }) },
  { key: "citizen_appeal_reminder", label: "Ciudadano · Recordatorio del recurso (80 %)",
    build: (t) => t.citizenAppealReminder({
      radicado: "2026-000123", appealLink: "#", appealLastDay: new Date(Date.now() + 2 * 864e5),
    }) },
  { key: "citizen_appeal_received", label: "Ciudadano · Recurso recibido",
    build: (t) => t.citizenAppealReceived({
      radicado: "2026-000123", magicLink: "#", decisionDueAt: new Date(Date.now() + 60 * 864e5),
    }) },
  { key: "citizen_appeal_decided", label: "Ciudadano · Recurso resuelto",
    build: (t) => t.citizenAppealDecided({
      radicado: "2026-000123", magicLink: "#", granted: true, newDeadline: new Date(Date.now() + 6 * 864e5),
      reason: "La información aportada completa la petición.",
    }) },
  { key: "citizen_desistimiento_final", label: "Ciudadano · Archivo en firme",
    build: (t) => t.citizenDesistimientoFinal({
      radicado: "2026-000123", magicLink: "#", appealLastDay: new Date(Date.now() - 864e5),
    }) },
  { key: "staff_appeal_filed", label: "Funcionario · Recurso por resolver",
    build: (t) => t.staffAppealFiled({
      radicado: "2026-000123", adminLink: "#", decisionDueAt: new Date(Date.now() + 60 * 864e5),
    }) },
  { key: "staff_assigned", label: "Funcionario · PQRSD asignada",
    build: (t) => t.staffAssigned({ radicado: "2026-000123", subjectLine: "Ruido en la vía pública", type: "queja", slaDeadline: new Date(Date.now() + 12 * 864e5) }) },
];

function EmailPreview({ brandColor, brandName, logoUrl, logoWidth }) {
  // The preview reads like the real email: same module name as the functions use.
  emailTemplates.setModuleLabel(getPqrsdLabel());
  const [tplKey, setTplKey] = useState(PREVIEW_TEMPLATES[0].key);

  const { subject, html } = useMemo(() => {
    const tpl = PREVIEW_TEMPLATES.find((t) => t.key === tplKey) || PREVIEW_TEMPLATES[0];
    // Apply the picked brand, render, then reset so the override never leaks
    // into a real send that might happen later in this session.
    emailTemplates.setBrand({ color: brandColor, name: brandName, logo: logoUrl, logoWidth });
    const out = tpl.build(emailTemplates);
    emailTemplates.setBrand({});
    return out;
  }, [tplKey, brandColor, brandName, logoUrl, logoWidth]);

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>Vista previa de correos</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Así se verán las notificaciones con el color y nombre actuales. Cambia el color arriba para verlo reflejado al instante.
        </Typography>
        <TextField
          select fullWidth label="Plantilla" value={tplKey}
          onChange={(e) => setTplKey(e.target.value)} sx={{ mb: 2, maxWidth: 420 }}
        >
          {PREVIEW_TEMPLATES.map((t) => (
            <MenuItem key={t.key} value={t.key}>{pqrsdText(t.label)}</MenuItem>
          ))}
        </TextField>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Asunto: <strong>{subject}</strong>
        </Typography>
        <Box
          component="iframe" title="Vista previa del correo" srcDoc={html}
          sx={{ width: "100%", height: 560, border: "1px solid #e0e0e0", borderRadius: 1, bgcolor: "#f4f5f7" }}
        />
      </CardContent>
    </Card>
  );
}

function GroupCard({ group, onEdit, onDelete }) {
  const members = Array.isArray(group.members) ? group.members : [];
  return (
    <Card variant="outlined" sx={{ height: "100%", opacity: group.active === false ? 0.6 : 1 }}>
      <CardContent>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>{group.name}</Typography>
            {group.active === false && <Chip size="small" label="Inactiva" sx={{ mt: 0.5 }} />}
          </Box>
          <Box>
            <Tooltip title="Editar"><IconButton size="small" onClick={onEdit}><EditIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Eliminar"><IconButton size="small" onClick={onDelete}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
          </Box>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
          {group.description || "(sin descripción — la IA no podrá enrutar con precisión)"}
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="caption" color="text.secondary">
          {members.length} integrante{members.length === 1 ? "" : "s"}
        </Typography>
        <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {members.slice(0, 6).map((m) => (
            <Chip key={m.userId} size="small" label={m.name || m.email || m.userId} />
          ))}
          {members.length > 6 && <Chip size="small" label={`+${members.length - 6}`} />}
        </Box>
      </CardContent>
    </Card>
  );
}

function GroupDialog({ mode, group, users, onSyncSupervisorRoles, onClose, onSaved }) {
  const [name, setName] = useState(group?.name || "");
  const [description, setDescription] = useState(group?.description || "");
  const [active, setActive] = useState(group?.active !== false);
  const [members, setMembers] = useState(group?.members || []);
  const [supervisorIds, setSupervisorIds] = useState(group?.supervisorIds || []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Reconcile stored members against the live user list so the Autocomplete
  // shows full labels and equality works by userId.
  const value = useMemo(() => {
    const byId = new Map(users.map((u) => [u.userId, u]));
    return (members || []).map((m) => byId.get(m.userId) || { ...m });
  }, [members, users]);

  // Supervisors are chosen from the current members. Deriving the value from
  // `value` means dropping a member automatically drops them as a supervisor.
  const supervisorValue = useMemo(
    () => value.filter((u) => supervisorIds.includes(u.userId)),
    [value, supervisorIds]
  );

  const submit = async () => {
    if (!name.trim()) { setError("El nombre es obligatorio."); return; }
    setBusy(true);
    setError(null);
    try {
      const nextSupervisorIds = supervisorValue.map((u) => u.userId);
      const prevSupervisorIds = group?.supervisorIds || [];
      const added = nextSupervisorIds.filter((id) => !prevSupervisorIds.includes(id));
      const removed = prevSupervisorIds.filter((id) => !nextSupervisorIds.includes(id));
      const payload = {
        name,
        description,
        active,
        members: (value || []).map((u) => ({
          userId: u.userId,
          name: u.name ? `${u.name} ${u.lastName || ""}`.trim() : (u.email || null),
          email: u.email || null,
        })),
        supervisorIds: nextSupervisorIds,
      };
      if (mode === "create") {
        const newId = await createPqrsdGroup(payload);
        onSaved("Dependencia creada.");
        await onSyncSupervisorRoles?.({ added, removed, savedGroupId: newId });
      } else {
        await updatePqrsdGroup(group.id, payload);
        onSaved("Dependencia actualizada.");
        await onSyncSupervisorRoles?.({ added, removed, savedGroupId: group.id });
      }
    } catch (e) {
      setError(e?.message || "No fue posible guardar la dependencia.");
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{mode === "create" ? "Nueva dependencia" : "Editar dependencia"}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Nombre" value={name} onChange={(e) => setName(e.target.value)}
            fullWidth autoFocus placeholder="p. ej. Coordinación Jurídica"
          />
          <TextField
            label="Descripción (contexto para la IA)" value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth multiline minRows={4}
            placeholder="Describe qué tipo de solicitudes atiende esta dependencia: temas, trámites, palabras clave, casos típicos…"
            helperText="Cuanto más específica, mejor enruta la IA. El nombre por sí solo no basta."
          />
          <Autocomplete
            multiple
            options={users}
            getOptionLabel={(o) => `${o.name || ""} ${o.lastName || ""} <${o.email || ""}>`.trim()}
            isOptionEqualToValue={(o, v) => o?.userId === v?.userId}
            value={value}
            onChange={(_, v) => setMembers(v)}
            renderInput={(params) => <TextField {...params} label="Integrantes" />}
            noOptionsText="Sin resultados"
          />
          <Autocomplete
            multiple
            options={value}
            getOptionLabel={(o) => `${o.name || ""} ${o.lastName || ""} <${o.email || ""}>`.trim()}
            isOptionEqualToValue={(o, v) => o?.userId === v?.userId}
            value={supervisorValue}
            onChange={(_, v) => setSupervisorIds(v.map((u) => u.userId))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Supervisores de la dependencia"
                helperText={pqrsdText("Se les asigna automáticamente el rol Supervisor de dependencia PQRSD (y se les retira al quitarlos, si no lideran otra dependencia).")}
              />
            )}
            noOptionsText="Primero agrega integrantes"
          />
          <FormControlLabel
            control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
            label="Activa (disponible para enrutamiento por IA)"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancelar</Button>
        <Button onClick={submit} variant="contained" disabled={busy}>
          {busy ? "Guardando…" : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
